import { useCallback, useRef, useState } from "react";
import type { TReviewLineItem } from "./types";
import type { TApiLineItemQuery } from "@/apis/proda_finance/types";
import type { TPropertyCodeKind } from "@/modules/rev_apac/review/masterPropertyCodeSearch";

/** The mapped identifier an auto-populate writes: its master kind, line-item field and label. */
export type TAutoPopulateTarget = TPropertyCodeKind;

export type TAutoPopulateField =
    | "mapped_property_code"
    | "mapped_asset_code"
    | "mapped_investment_code";

const TARGET_DEFS: Record<TAutoPopulateTarget, { field: TAutoPopulateField; label: string }> = {
    entity: { field: "mapped_property_code", label: "Entity" },
    asset: { field: "mapped_asset_code", label: "Asset" },
    investment: { field: "mapped_investment_code", label: "Investment" },
};

/** The targets in display order, for the modal's select. */
export const AUTO_POPULATE_TARGETS: ReadonlyArray<{
    target: TAutoPopulateTarget;
    field: TAutoPopulateField;
    label: string;
}> = (["entity", "asset", "investment"] as const).map((target) => ({
    target,
    ...TARGET_DEFS[target],
}));

type TUseAutoPopulateDeps = {
    lineItems: TReviewLineItem[];
    filteredLineItems: TReviewLineItem[] | null;
    /** Server-side count of rows matching the active filter (whole dataset). */
    matchingRowCount: number;
    /** Active column filter mapped to API query params (no skip/limit). */
    activeFilterQuery: TApiLineItemQuery;
    onAutoPopulatePropcodes: (propcode: string, lineItemIds: string[]) => Promise<number>;
    onAutoPopulatePropcodesByFilter: (
        filterQuery: TApiLineItemQuery,
        propcode: string,
        field: TAutoPopulateField,
    ) => Promise<number>;
    /**
     * Server-side master property-code search: called with the typed query string and
     * the kind of master code the chosen target takes.
     */
    searchMasterPropertyCodes: (
        query: string,
        kind: TPropertyCodeKind,
    ) => Promise<Array<{ value: string; label: string }>>;
    onRefresh: () => void;
    /** Surface a success message (e.g. a toast/banner) once an auto-populate completes. */
    onSuccess?: (message: string) => void;
};

/**
 * Encapsulates the auto-populate modal: open/close state, the chosen target (the
 * mapped entity, asset or investment), lookup-option loading, and the submit handler.
 * Extracted from ViewFileReview.
 */
export function useAutoPopulate({
    lineItems: _lineItems,
    filteredLineItems: _filteredLineItems,
    matchingRowCount,
    activeFilterQuery,
    onAutoPopulatePropcodes: _onAutoPopulatePropcodes,
    onAutoPopulatePropcodesByFilter,
    searchMasterPropertyCodes,
    onRefresh,
    onSuccess,
}: TUseAutoPopulateDeps) {
    const [isAutoPopulateOpen, setIsAutoPopulateOpen] = useState(false);
    const [autoPopulateTarget, setAutoPopulateTargetState] =
        useState<TAutoPopulateTarget>("entity");
    const { field: targetField, label: targetLabel } = TARGET_DEFS[autoPopulateTarget];
    // Full selected option (label "code - description") for the code SearchableSelect.
    const [selectedPropcodeOption, setSelectedPropcodeOption] = useState<{
        value: string;
        label: string;
    } | null>(null);
    const selectedPropcode = selectedPropcodeOption?.value ?? "";
    const [autoPopulateError, setAutoPopulateError] = useState<string | null>(null);
    const [isAutoPopulating, setIsAutoPopulating] = useState(false);

    // Keep the latest callback refs so effects don't need to re-run when the
    // parent passes a new function identity on each render (e.g. in tests or
    // when useCases() is called inside the component body).
    const searchMasterPropertyCodesRef = useRef(searchMasterPropertyCodes);
    searchMasterPropertyCodesRef.current = searchMasterPropertyCodes;

    const handleAutoPopulateOpenChange = useCallback((open: boolean) => {
        setIsAutoPopulateOpen(open);
        if (!open) {
            setSelectedPropcodeOption(null);
            setAutoPopulateError(null);
            setAutoPopulateTargetState("entity");
        }
    }, []);

    // A code picked for one kind must not be sent as another, so switching target
    // drops the selection.
    const setAutoPopulateTarget = useCallback((target: TAutoPopulateTarget) => {
        setAutoPopulateTargetState(target);
        setSelectedPropcodeOption(null);
        setAutoPopulateError(null);
    }, []);

    /**
     * Search callback for the code SearchableSelect: delegates to the server-side
     * master property-code search, scoped to the chosen target's kind, so the query
     * matches the company's whole master set of that kind (code OR name).
     */
    const searchPropcodesForModal = useCallback(
        (query: string) => searchMasterPropertyCodesRef.current(query, autoPopulateTarget),
        [autoPopulateTarget],
    );

    const handleAutoPopulatePropcodes = useCallback(async () => {
        if (!selectedPropcode) {
            setAutoPopulateError(
                `Select a mapped ${targetLabel.toLowerCase()} before auto-populating.`,
            );
            return;
        }
        if (matchingRowCount === 0) {
            setAutoPopulateError("No line items match the current filter.");
            return;
        }
        setIsAutoPopulating(true);
        setAutoPopulateError(null);
        try {
            const updated = await onAutoPopulatePropcodesByFilter(
                activeFilterQuery,
                selectedPropcode,
                targetField,
            );
            handleAutoPopulateOpenChange(false);
            onSuccess?.(
                `Mapped ${targetLabel} auto-populated on ${updated} line item${updated === 1 ? "" : "s"}.`,
            );
            onRefresh();
        } catch (error) {
            setAutoPopulateError(
                error instanceof Error ? error.message : "Failed to auto-populate.",
            );
        } finally {
            setIsAutoPopulating(false);
        }
    }, [
        selectedPropcode,
        targetField,
        targetLabel,
        matchingRowCount,
        activeFilterQuery,
        onAutoPopulatePropcodesByFilter,
        handleAutoPopulateOpenChange,
        onRefresh,
        onSuccess,
    ]);

    return {
        isAutoPopulateOpen,
        setIsAutoPopulateOpen,
        handleAutoPopulateOpenChange,
        autoPopulateTarget,
        /** Display label of the chosen target ("Entity", "Asset" or "Investment"). */
        autoPopulateTargetLabel: targetLabel,
        setAutoPopulateTarget,
        selectedPropcode,
        selectedPropcodeOption,
        setSelectedPropcodeOption,
        autoPopulateError,
        isAutoPopulating,
        handleAutoPopulatePropcodes,
        /** Search callback for the code SearchableSelect — server-side master search of the chosen target's kind. */
        searchPropcodesForModal,
    };
}
